/**
 * Canonical Lease type - matches the `leases` Firestore collection.
 *
 * Monetary values are in **dollars** (not cents). InvoiceService converts
 * to cents when building Stripe PaymentIntents.
 *
 * `ownerId` is the canonical owner field written by all create paths.
 * `landlordId` is kept as an optional alias for backward compat.
 */

export interface Lease {
  id: string;
  ownerId: string;
  /** @deprecated Use ownerId. Kept so legacy docs still type-check. */
  landlordId?: string;
  tenantId: string;
  tenantEmail?: string;
  propertyId?: string;
  unitId?: string;

  rentAmount: number; // dollars
  securityDeposit?: number; // dollars
  rentDueDay?: number; // 1-31

  startDate?: string; // YYYY-MM-DD or ISO string
  endDate?: string; // YYYY-MM-DD or ISO string

  status:
    | "draft"
    | "pending_signature"
    | "active"
    | "expired"
    | "terminated"
    | "renewed"
    | "month_to_month"
    | "ending"
    | "not_renewing";

  /** @deprecated Prefer `status === "active"`. */
  active?: boolean;
  autoRenew?: boolean;
  monthToMonthAfterEnd?: boolean;

  /** DocuSeal e-signature */
  docuSealSubmissionId?: number;
  signedAt?: string;
  signedLeaseStoragePath?: string;
  docuSealAuditLogUrl?: string;

  /** Termination */
  terminatedAt?: string;
  terminationReason?: string;
  predecessorLeaseId?: string;

  lateFeePolicy?: {
    gracePeriodDays: number;
    lateFeeAmountCents: number;
    lateFeeType: "fixed" | "percentage";
  };

  /** Ingestion / draft flags */
  ingestion_status?: string;
  is_draft?: boolean;
  is_pending_review?: boolean;

  createdAt: string;
  updatedAt: string;

  [key: string]: unknown;
}

/** Resolve owner id from either field name (handles legacy docs). */
export function getLeaseOwnerId(lease: Partial<Lease>): string {
  return lease.ownerId || lease.landlordId || "";
}

/** Resolve monthly rent in dollars from any document shape. */
export function getLeaseRentDollars(lease: Record<string, unknown>): number {
  if (typeof lease.rentAmount === "number") return lease.rentAmount;
  if (typeof lease.monthlyRentCents === "number")
    return (lease.monthlyRentCents as number) / 100;
  if (typeof lease.monthlyRent === "number") return lease.monthlyRent as number;
  return 0;
}

/** True when the lease is currently in force. */
export function isLeaseActive(lease: Partial<Lease>): boolean {
  const st = lease.status;
  if (
    st === "terminated" ||
    st === "expired" ||
    st === "renewed" ||
    st === "ending" ||
    st === "not_renewing"
  ) {
    return false;
  }
  return lease.status === "active" || lease.active === true;
}
